/**
 * Header メガメニュー Service ブロックの階層タブ (Service / Solution / Product)
 * 切替スクリプト。
 *
 * - WAI-ARIA Tabs Pattern (vertical) 準拠 — role=tablist / tab / tabpanel
 * - Automatic activation: 矢印 ↑↓ / Home / End キーで focus 移動と同時にパネル切替
 *   (パネルは事前 SSR で軽量のため Auto を採用)
 * - クリックでも切替
 * - 初期化時に非アクティブパネルへ `hidden` を動的付与 (JS 無効環境では PHP 側
 *   で `hidden` 未指定 → 3 パネル全て可視のフォールバック表示)
 * - data-bg-image 属性経由で背景画像 URL を CSS custom property に注入
 *   (PHP 側のインライン style="" を回避、CSP `style-src` 厳格化対応)
 * - メガメニュー自体の開閉は既存 src/js/header.js が制御。本スクリプトは
 *   開閉トリガーに依存せず、DOMContentLoaded で 1 度だけ初期化する。
 */

import { gsap } from 'gsap';

/**
 * 1 つの `[data-mega-menu-service]` ルート要素について Tabs Pattern を初期化する。
 *
 * 旧実装は `document.querySelector` で先頭 1 個だけ初期化していたため、将来 SP
 * メニューで同じパーシャルを再利用した場合 (= ページ内に複数 root が存在) に
 * 2 個目以降が無反応になる潜在バグがあった。`initAll()` から各 root に対して
 * 呼び出す形に整理し、複数描画にも対応する。
 *
 * @param {HTMLElement} root - data-mega-menu-service 属性を持つコンテナ要素
 */
function initHeaderMegamenuService(root) {
  if (!root) return;

  const tablist = root.querySelector('[role="tablist"]');
  if (!tablist) return;

  const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
  const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
  if (!tabs.length || !panels.length) return;

  // 背景画像: data-bg-image を CSS 変数 --mega-bg-image に転写 (PHP インライン
  // style="" を回避)。data-bg-image 未設定のパネルは未注入のまま、CSS の
  // radial-gradient フォールバックが見える。
  panels.forEach(panel => {
    const bg = panel.dataset.bgImage;
    if (bg) {
      panel.style.setProperty('--mega-bg-image', `url("${bg}")`);
    }
  });

  // 初期化: アクティブ tab (`is-active`) 以外のパネルへ hidden を付与。
  // PHP 側で hidden を直書きせず、JS 起動時にこの分岐で付与することで
  // JS 無効環境では 3 パネル全て可視のフォールバックが効く。
  const initialActiveTab =
    tabs.find(t => t.classList.contains('is-active')) || tabs[0];
  const initialKey = initialActiveTab.dataset.tabKey;
  panels.forEach(panel => {
    panel.hidden = panel.dataset.tabKey !== initialKey;
  });

  function reset() {
    const firstKey = tabs[0]?.dataset.tabKey;
    tabs.forEach(tab => {
      const isFirst = tab === tabs[0];
      tab.classList.toggle('is-active', isFirst);
      tab.setAttribute('aria-selected', isFirst ? 'true' : 'false');
      tab.tabIndex = isFirst ? 0 : -1;
    });
    panels.forEach(panel => {
      gsap.killTweensOf(panel);
      gsap.set(panel, { clearProps: 'opacity' });
      panel.hidden = panel.dataset.tabKey !== firstKey;
    });
  }

  /**
   * 指定タブをアクティブ化。
   * @param {HTMLElement} targetTab
   * @param {{focus?: boolean}} [opts]
   */
  function activate(targetTab, opts = {}) {
    if (targetTab.getAttribute('aria-disabled') === 'true') return;
    const { focus = true } = opts;
    const key = targetTab.dataset.tabKey;
    tabs.forEach(tab => {
      const isActive = tab === targetTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });
    panels.forEach(panel => {
      const isActive = panel.dataset.tabKey === key;
      gsap.killTweensOf(panel);
      if (isActive) {
        gsap.set(panel, { opacity: 0 });
        panel.hidden = false;
        gsap.to(panel, { opacity: 1, duration: 0.25, ease: 'power1.out' });
      } else if (!panel.hidden) {
        gsap.to(panel, {
          opacity: 0,
          duration: 0.15,
          ease: 'power1.out',
          onComplete: () => {
            panel.hidden = true;
            gsap.set(panel, { clearProps: 'opacity' });
          },
        });
      }
    });
    if (focus) targetTab.focus();
  }

  // タブは <button type="button"> なのでデフォルト挙動 (form submit / navigation) は無く、
  // e.preventDefault() は不要。activate() のみ呼ぶ。
  tabs.forEach(tab => {
    tab.addEventListener('click', () => activate(tab));
  });

  // 矢印キー: vertical タブなので ↑↓ で前後移動 (SP の横並び時は ←→ にも対応)。
  // listener は tablist に限定 (パネル内 link でも root に bubble して反応する
  // のを防ぐ; パネル内の Tab/Arrow は通常のフォーカス遷移に委ねる)。
  tablist.addEventListener('keydown', e => {
    if (
      ![
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
      ].includes(e.key)
    ) {
      return;
    }
    const currentIdx = tabs.findIndex(t => t === document.activeElement);
    if (currentIdx === -1) return;
    let nextIdx = currentIdx;
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      nextIdx = (currentIdx - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      nextIdx = (currentIdx + 1) % tabs.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = tabs.length - 1;
    }
    e.preventDefault();
    activate(tabs[nextIdx]);
  });

  // メガメニューが再オープンされるたびに最初のタブ (Service) へリセット。
  // header.js の openSubmenu() が l-header__submenu--service へ CustomEvent を
  // dispatch するので、closest() で親 submenu 要素を特定して listen する。
  const serviceSubmenu = root.closest('.l-header__submenu--service');
  serviceSubmenu?.addEventListener('megamenu-service:open', () => {
    reset();
  });
}

/**
 * ページ内の全 [data-mega-menu-service] root に対して初期化を実行する。
 * `querySelectorAll` を使うことで複数 root (PC + 将来の SP) に同時対応する。
 */
function initAll() {
  const roots = document.querySelectorAll('[data-mega-menu-service]');
  roots.forEach(initHeaderMegamenuService);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
